import { Chart, Element, Point } from 'chart.js';
import { FORMAT_TYPE } from 'cyprus/enums';
import { formatValue } from 'cyprus/formats';
import { colors } from 'framework/theme/constants/colors';
import moment from 'moment';

import {
  ChartArgsCrosshair,
  ChartCrosshair,
  CrosshairOptions,
  CrosshairPlugin,
  NearestPoint,
} from './types';
import { drawChip, drawLine } from './utils';

export const crosshairPlugin: CrosshairPlugin = {
  id: 'crosshairs',
  defaults: {
    enabled: false,
    snap: false,
    snapThreshold: 20,
  },

  afterInit(chart: ChartCrosshair) {
    chart.crosshair = { x: null, y: null, point: null };
  },

  beforeEvent(chart: ChartCrosshair, args: ChartArgsCrosshair) {
    const { event } = args;
    const options = chart.options.plugins?.crosshairs;

    if (!options?.enabled || !event.native) return;

    const rect = chart.canvas.getBoundingClientRect();
    const x = event.native.clientX - rect.left;
    const y = event.native.clientY - rect.top;

    if (event.type === 'mousemove') {
      if (options.snap) {
        const nearestPoint = findNearestDataPoint(chart, x, y, options.snapThreshold);
        if (nearestPoint) {
          chart.crosshair = { x: nearestPoint.x, y: nearestPoint.y, point: nearestPoint.point };
        } else {
          chart.crosshair = { x, y, point: null };
        }
      } else {
        chart.crosshair = { x, y, point: null };
      }
    } else if (event.type === 'mouseout') {
      chart.crosshair = { x: null, y: null, point: null };
    }
    chart.draw();
  },

  afterDraw(chart: ChartCrosshair, _args: unknown, options: CrosshairOptions) {
    if (!options?.enabled) return;

    const { ctx, chartArea, scales } = chart;
    const { crosshair } = chart;
    const { x, y } = crosshair;

    if (x === null || y === null || !chartArea) return;

    const { left, right, top, bottom } = chartArea;

    if (x < left || x > right || y < top || y > bottom) return;

    ctx.save();

    // vertical line
    drawLine(ctx, x, top, x, bottom, 'rgba(100, 100, 100, 0.75)');

    // horizontal line
    drawLine(ctx, left, y, right, y, 'rgba(100, 100, 100, 0.75)');

    // price
    const value = scales.line.getValueForPixel(y);
    const price = formatValue(value, { format: FORMAT_TYPE.CURRENCY });
    drawChip(ctx, price, right, y, false, { background: colors.dark[9], border: colors.dark[7] });

    // time label at bottom
    const time = scales.x.getValueForPixel(x);
    const formatted = moment(time).utc().format('M/D/YY hh:mm A');
    drawChip(ctx, formatted, x, bottom, true, {
      background: colors.dark[9],
      border: colors.dark[7],
    });

    ctx.restore();
  },
};

function findNearestDataPoint(
  chart: Chart,
  mouseX: number,
  mouseY: number,
  threshold = 20
): NearestPoint | null {
  let nearestPoint: NearestPoint | null = null;
  let minDistance = threshold;

  chart.data.datasets.forEach((dataset, datasetIndex) => {
    const meta = chart.getDatasetMeta(datasetIndex);
    const data = dataset.data;

    meta.data.forEach((element: Element, index) => {
      const props = element.getProps(['x', 'y']) as Point;
      const currentData = data[index];

      // Skip if data point is not a number
      if (typeof currentData !== 'number') return;

      // Prioritize vertical distance for snapping
      const horizontalDistance = Math.abs(mouseX - props.x);

      // Only consider points that are within the horizontal threshold
      const distance = horizontalDistance <= threshold ? horizontalDistance : Infinity;

      if (distance < minDistance) {
        minDistance = distance;
        nearestPoint = {
          x: props.x,
          y: props.y,
          point: { datasetIndex, index, value: currentData },
        };
      }
    });
  });

  return nearestPoint;
}
